import { io } from 'socket.io-client';
import api from './api';

const SOCKET_URL = import.meta.env.VITE_API_BASE_URL;

class ChatService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.userId = null;
  }

  connect(userId) {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.userId = userId;
    
    const url = `${SOCKET_URL}/chat`;
    
    this.socket = io(url, {
      query: { userId },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      autoConnect: true,
    });

    this.setupEventListeners();
    return this.socket;
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  disconnect() {
    if (this.socket) {
      console.log('🔌 [ChatService] Desconectando...');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      this.userId = null;
    }
  }

  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.emit('connected', { socketId: this.socket.id });
    });

    this.socket.on('disconnect', (reason) => {
      this.emit('disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔥 [ChatService] Erro:', error.message);
      this.emit('error', error);
    });

    // EVENTOS DO CHAT
    this.socket.on('usuarios_ativos', (data) => {
      this.emit('usuarios_ativos', data);
    });

    this.socket.on('conversas', (data) => {
      this.emit('conversas', data);
    });

    this.socket.on('nova_mensagem', (data) => {
      this.emit('nova_mensagem', data);
    });

    this.socket.on('mensagem_enviada', (data) => {
      this.emit('mensagem_enviada', data);
    });

    this.socket.on('historico', (data) => {
      this.emit('historico', data);
    });

    this.socket.on('usuario_online', (data) => {
      this.emit('usuario_online', data);
    });

    this.socket.on('usuario_offline', (data) => {
      this.emit('usuario_offline', data);
    });

    this.socket.on('usuario_digitando', (data) => {
      this.emit('usuario_digitando', data);
    });

    this.socket.on('mensagem_lida', (data) => {
      this.emit('mensagem_lida', data);
    });

    this.socket.on('erro', (data) => {
      console.error('❌ [ChatService] Erro servidor:', data);
      this.emit('erro', data);
    });
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) callbacks.splice(index, 1);
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    this.listeners.get(event).forEach(callback => callback(data));
  }

  // AÇÕES DO SOCKET
  enviarMensagem(remetenteId, mensagem) {
    if (!this.socket?.connected) {
      console.error('❌ [ChatService] Socket não conectado!');
      throw new Error('Socket não conectado');
    }
    this.socket.emit('enviar_mensagem', { remetenteId, mensagem });
  }

  buscarHistorico(usuarioId, outroUsuarioId, pagina = 1, limite = 50) {
    if (!this.socket?.connected) {
      console.error('❌ [ChatService] Socket não conectado!');
      throw new Error('Socket não conectado');
    }
    this.socket.emit('buscar_historico', {
      usuarioId,
      outroUsuarioId,
      pagina,
      limite,
    });
  }

  digitando(usuarioId, destinatarioId, digitando) {
    if (!this.socket?.connected) return;
    this.socket.emit('digitando', { usuarioId, destinatarioId, digitando });
  }

  // API HTTP
  async buscarConversas(usuarioId) {
    try {
      const response = await api.get(`/chat/conversas/${usuarioId}`);
      return response.data;
    } catch (error) {
      console.error('❌ [ChatService] Erro ao buscar conversas:', error);
      throw error;
    }
  }

  async buscarUsuariosDisponiveis(usuarioId, termo = '') {
    try {
      const response = await api.get(`/chat/usuarios/${usuarioId}`, {
        params: { termo },
      });
      return response.data;
    } catch (error) {
      console.error('❌ [ChatService] Erro ao buscar usuários:', error);
      throw error;
    }
  }

  async marcarTodasLidas(usuarioId, outroUsuarioId) {
    try {
      const response = await api.put(`/chat/conversas/${outroUsuarioId}/lida`, {
        usuarioId,
        outroUsuarioId,
      });
      return response.data;
    } catch (error) {
      console.error('❌ [ChatService] Erro ao marcar todas lidas:', error);
      throw error;
    }
  }

  async buscarUsuariosOnline() {
    try {
      const response = await api.get('/chat/usuarios-online');
      return response.data;
    } catch (error) {
      console.error('❌ [ChatService] Erro ao buscar online:', error);
      throw error;
    }
  }

  async criarReport(data) {
    try {
      const response = await api.post('/chat/reports', data);
      return response.data;
    } catch (error) {
      console.error('❌ [ChatService] Erro ao criar report:', error);
      throw error;
    }
  }
}

export default new ChatService();